import type { ReactNode } from 'react'
import styles from './BaseKanbanBoard.module.css'
import { CountBadge } from '@/components/ui/count-badge'
import { InfiniteScrollSentinel } from '@/components/ui/infinite-scroll-sentinel'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'

export interface BaseKanbanColumn<TItem> {
  id: string
  title: string
  dotColor: string
  items: TItem[]
  emptyStateText?: string
  /**
   * Optional infinite-scroll wiring. When `onLoadMore` is set, a sentinel is
   * rendered at the bottom of the column's scroll area and `totalCount` (not
   * the loaded `items.length`) drives the header badge.
   */
  totalCount?: number
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  onLoadMore?: () => void
}

interface BaseKanbanBoardProps<TItem> {
  columns: BaseKanbanColumn<TItem>[]
  getItemKey: (item: TItem) => string
  renderCard: (item: TItem, column: BaseKanbanColumn<TItem>) => ReactNode
  renderColumnAction?: (column: BaseKanbanColumn<TItem>) => ReactNode
  columnTestIdPrefix?: string
  fillParent?: boolean
}

function columnRole(id: string): 'active' | 'quiet' | 'default' {
  if (id === 'in_progress') return 'active'
  if (id === 'completed') return 'quiet'
  return 'default'
}

export function BaseKanbanBoard<TItem>({
  columns,
  getItemKey,
  renderCard,
  renderColumnAction,
  columnTestIdPrefix,
  fillParent = false,
}: BaseKanbanBoardProps<TItem>) {
  return (
    <div className={cn(styles.columnsGrid, fillParent && styles.columnsGridFillParent)}>
      {columns.map((column) => (
        <div
          key={column.id}
          className={styles.column}
          data-column-role={columnRole(column.id)}
          data-testid={columnTestIdPrefix ? `${columnTestIdPrefix}${column.id}` : undefined}
        >
          <div
            className={styles.columnHeader}
            data-testid={columnTestIdPrefix ? `${columnTestIdPrefix}${column.id}-header` : undefined}
          >
            <div className={styles.columnHeaderLeft}>
              <span className={styles.statusDot} style={{ background: column.dotColor }} />
              <span className={styles.columnTitle}>{column.title}</span>
              <CountBadge
                variant="subtle"
                count={column.totalCount ?? column.items.length}
                className="h-[18px] min-w-[18px] bg-white border-0 shrink-0"
                aria-label={`${column.totalCount ?? column.items.length} items`}
              />
            </div>
            {renderColumnAction ? (
              <div className={styles.columnHeaderAction}>{renderColumnAction(column)}</div>
            ) : null}
          </div>

          {column.items.length === 0 ? (
            <div className={styles.emptyColumn}>{column.emptyStateText || 'Nothing here yet'}</div>
          ) : (
            <ScrollArea className={styles.columnScroll}>
              <div className={styles.cardsList}>
                {column.items.map((item) => (
                  <div key={getItemKey(item)}>{renderCard(item, column)}</div>
                ))}
              </div>
              {column.onLoadMore ? (
                <InfiniteScrollSentinel
                  hasNextPage={!!column.hasNextPage}
                  isFetching={!!column.isFetchingNextPage}
                  onLoadMore={column.onLoadMore}
                />
              ) : null}
            </ScrollArea>
          )}
        </div>
      ))}
    </div>
  )
}
